import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import type {Result} from "@vesta/error-handling-remix/result";
import type {ComplexFilter} from "@vesta/vos-types/modules/workspaceLevel/domain/ComplexFilter";
import {
    ottopilotEventContainerValidator,
    ottopilotEventContainerWithEventsValidator,
    type OttopilotEventContainerWithEvents,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {ottopilotEventValidator} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import {
    WorkspaceCapabilityStatus,
    type WorkspaceCapability,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import type {WorkspaceCapabilityAccessMatrix} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityAccessMatrix";
import {EventContainerAndEventOrchestrationWorkspaceCapabilityAccessOperation} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/eventContainerAndEventOrchestrationWorkspaceCapability/domain/EventContainerAndEventOrchestrationWorkspaceCapabilityAccessOperation";
import {z as zod} from "zod";
import type {EventWithEventContainer} from "~/domainModels/eventContainers";
import type {FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import type {SearchConfiguration} from "~/components/ottopilotDataViewer/searchConfiguration";
import type {SortConfiguration} from "~/components/ottopilotDataViewer/sortConfiguration";
import {
    convertFilterConfigurationToSrimanFilters,
    convertFilterConfigurationToSrimanFiltersForEvents,
    convertSortConfigurationToSrimanOrderBy,
    convertSortConfigurationToSrimanOrderByForEvents,
} from "~/utilities/apis/api";

type OrchestrationApiContext = {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
};

type CalendarOrderBy = Array<{
    column: string;
    direction: "asc" | "desc";
}>;

type OrchestratedListQuery = {
    columns: Array<string> | null;
    filters?: ComplexFilter;
    limit?: number;
    maxRowsPerDay: number | null;
    maxRowsPerDayColumn: string | null;
    offset?: number;
    orderBy?: CalendarOrderBy;
    templateId: Uuid;
};

export function buildOrchestratedListRequestBody(
    query: OrchestratedListQuery & {eventColumns?: Array<string>; id?: Uuid},
): Record<string, unknown> {
    return {
        columns: query.columns,
        ...(query.eventColumns === undefined ? {} : {eventColumns: query.eventColumns}),
        ...(query.filters === undefined ? {} : {filters: query.filters}),
        ...(query.id === undefined ? {} : {id: query.id}),
        ...(query.limit === undefined ? {} : {limit: query.limit}),
        maxRowsPerDay: query.maxRowsPerDay,
        maxRowsPerDayColumn: query.maxRowsPerDayColumn,
        ...(query.offset === undefined ? {} : {offset: query.offset}),
        ...(query.orderBy === undefined ? {} : {orderBy: query.orderBy}),
        templateId: query.templateId,
    };
}

const countByColumnRowValidator = zod.array(
    zod.strictObject({
        count: zod.number(),
        value: zod.string().nullable(),
    }),
);

const eventWithEventContainerValidator = ottopilotEventValidator.extend({
    eventContainer: ottopilotEventContainerValidator,
});

function getHeaders(query: OrchestrationApiContext): Record<string, string> {
    return {
        Authorization: `Bearer ${query.accessToken}`,
        "Content-Type": "application/json",
        "X-Workspace-Id": query.workspaceId,
    };
}

export function isEventContainerAndEventOrchestrationEnabled(
    workspaceCapabilities: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>,
): boolean {
    return workspaceCapabilities.some(
        (capability) =>
            capability.type === WorkspaceCapabilityType.eventContainerAndEventOrchestration &&
            capability.status === WorkspaceCapabilityStatus.enabled,
    );
}

export type CalendarOrchestrationEligibility = "loading" | "absent" | "disabled" | "allowed" | "denied";
export type CalendarQueryMode = "loading" | "denied" | "unavailable" | "legacy" | "orchestration";

export function resolveCalendarOrchestrationEligibility({
    workspaceCapabilities,
    workspaceCapabilityAccessMatrix,
}: {
    workspaceCapabilities: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>> | undefined;
    workspaceCapabilityAccessMatrix: WorkspaceCapabilityAccessMatrix | undefined;
}): CalendarOrchestrationEligibility {
    if (workspaceCapabilities === undefined) {
        return "loading";
    }
    const capability = workspaceCapabilities.find(
        (item) => item.type === WorkspaceCapabilityType.eventContainerAndEventOrchestration,
    );
    if (capability === undefined) {
        return "absent";
    }
    if (capability.status !== WorkspaceCapabilityStatus.enabled) {
        return "disabled";
    }
    if (workspaceCapabilityAccessMatrix === undefined) {
        return "loading";
    }
    return workspaceCapabilityAccessMatrix[WorkspaceCapabilityType.eventContainerAndEventOrchestration as Uuid]?.[
        EventContainerAndEventOrchestrationWorkspaceCapabilityAccessOperation.read as Uuid
    ] === true
        ? "allowed"
        : "denied";
}

export function resolveCalendarQueryMode({
    eligibility,
    requiresOrchestration,
}: {
    eligibility: CalendarOrchestrationEligibility;
    requiresOrchestration: boolean;
}): CalendarQueryMode {
    if (eligibility === "allowed") return "orchestration";
    if (eligibility === "loading") return "loading";
    if (eligibility === "denied") return "denied";
    return requiresOrchestration === true ? "unavailable" : "legacy";
}

export async function countOrchestratedEventContainersByColumn(
    query: OrchestrationApiContext & {
        bucket: "day" | null;
        column: string;
        filters?: ComplexFilter;
        templateId: Uuid;
    },
): Promise<Result<Array<{count: number; value: string | null}>>> {
    return await robustJsonResultClientFetch(
        `${query.intellsysBaseUrl}/sub-system/pluto/event-container-and-event-orchestration/event-containers/count-by-column`,
        {
            body: JSON.stringify({
                bucket: query.bucket,
                column: query.column,
                ...(query.filters === undefined ? {} : {filters: query.filters}),
                templateId: query.templateId,
            }),
            headers: getHeaders(query),
            method: "POST",
        },
        countByColumnRowValidator,
    );
}

export async function countOrchestratedEventsByColumn(
    query: OrchestrationApiContext & {
        bucket: "day" | null;
        column: string;
        filters?: ComplexFilter;
        templateId: Uuid;
    },
): Promise<Result<Array<{count: number; value: string | null}>>> {
    return await robustJsonResultClientFetch(
        `${query.intellsysBaseUrl}/sub-system/pluto/event-container-and-event-orchestration/events/count-by-column`,
        {
            body: JSON.stringify({
                bucket: query.bucket,
                column: query.column,
                ...(query.filters === undefined ? {} : {filters: query.filters}),
                templateId: query.templateId,
            }),
            headers: getHeaders(query),
            method: "POST",
        },
        countByColumnRowValidator,
    );
}

export async function getOrchestratedEventContainers(
    query: OrchestrationApiContext & {
        columns: Array<string> | null;
        eventColumns?: Array<string>;
        filters?: ComplexFilter;
        id?: Uuid;
        limit?: number;
        maxRowsPerDay: number | null;
        maxRowsPerDayColumn: string | null;
        offset?: number;
        orderBy?: CalendarOrderBy;
        templateId: Uuid;
    },
): Promise<Result<Array<OttopilotEventContainerWithEvents>>> {
    return await robustJsonResultClientFetch(
        `${query.intellsysBaseUrl}/sub-system/pluto/event-container-and-event-orchestration/event-containers/events/get-all`,
        {
            body: JSON.stringify(buildOrchestratedListRequestBody(query)),
            headers: getHeaders(query),
            method: "POST",
        },
        zod.array(ottopilotEventContainerWithEventsValidator),
    );
}

export async function getOrchestratedEvents(
    query: OrchestrationApiContext & {
        columns: Array<string> | null;
        filters?: ComplexFilter;
        limit?: number;
        maxRowsPerDay: number | null;
        maxRowsPerDayColumn: string | null;
        offset?: number;
        orderBy?: CalendarOrderBy;
        templateId: Uuid;
    },
): Promise<Result<Array<EventWithEventContainer>>> {
    return await robustJsonResultClientFetch(
        `${query.intellsysBaseUrl}/sub-system/pluto/event-container-and-event-orchestration/events/event-containers/get-all`,
        {
            body: JSON.stringify(buildOrchestratedListRequestBody(query)),
            headers: getHeaders(query),
            method: "POST",
        },
        zod.array(eventWithEventContainerValidator),
    );
}

type OrchestratedEventFilterQuery = OrchestrationApiContext & {
    eventContainerAdditionalColumnIds?: Array<Uuid>;
    eventTemplateAdditionalColumnIds?: Array<Uuid>;
    filterConfiguration: FilterConfiguration;
    searchConfiguration?: SearchConfiguration;
    templateId: Uuid;
};

type OrchestratedEventQuery = OrchestratedEventFilterQuery & {
    columns: Array<string> | null;
    limit: number;
    maxRowsPerDay: number | null;
    maxRowsPerDayColumn: string | null;
    offset: number;
    sortConfiguration: SortConfiguration;
};

function toOrchestratedEventFilters(query: OrchestratedEventFilterQuery): ComplexFilter {
    return query.eventTemplateAdditionalColumnIds !== undefined && query.eventContainerAdditionalColumnIds !== undefined
        ? convertFilterConfigurationToSrimanFiltersForEvents(
              query.filterConfiguration,
              query.eventTemplateAdditionalColumnIds,
              query.eventContainerAdditionalColumnIds,
              query.searchConfiguration,
          )
        : convertFilterConfigurationToSrimanFilters(query.filterConfiguration, query.searchConfiguration);
}

function toOrchestratedEventOrderBy(query: OrchestratedEventQuery): CalendarOrderBy {
    const orderBy = query.eventTemplateAdditionalColumnIds !== undefined && query.eventContainerAdditionalColumnIds !== undefined
        ? convertSortConfigurationToSrimanOrderByForEvents(
              query.sortConfiguration,
              query.eventTemplateAdditionalColumnIds,
              query.eventContainerAdditionalColumnIds,
          )
        : convertSortConfigurationToSrimanOrderBy(query.sortConfiguration);
    return orderBy.map((item) => ({
        column: item.column,
        direction: item.direction === "asc" ? "asc" : "desc",
    }));
}

export async function getOrchestratedEventsWithEventContainers(
    query: OrchestratedEventQuery,
): Promise<Result<Array<EventWithEventContainer>>> {
    return await getOrchestratedEvents({
        accessToken: query.accessToken,
        columns: query.columns,
        filters: toOrchestratedEventFilters(query),
        intellsysBaseUrl: query.intellsysBaseUrl,
        limit: query.limit,
        maxRowsPerDay: query.maxRowsPerDay,
        maxRowsPerDayColumn: query.maxRowsPerDayColumn,
        offset: query.offset,
        orderBy: toOrchestratedEventOrderBy(query),
        templateId: query.templateId,
        workspaceId: query.workspaceId,
    });
}

export async function countOrchestratedEventsByFilterConfiguration(
    query: OrchestratedEventFilterQuery & {
        bucket: "day" | null;
        column: string;
    },
): Promise<Result<Array<{count: number; value: string | null}>>> {
    return await countOrchestratedEventsByColumn({
        accessToken: query.accessToken,
        bucket: query.bucket,
        column: query.column,
        filters: toOrchestratedEventFilters(query),
        intellsysBaseUrl: query.intellsysBaseUrl,
        templateId: query.templateId,
        workspaceId: query.workspaceId,
    });
}
